import { ErrorPage } from 'admin';
import { AiModelEnum } from 'api';
import { type ComponentProps, useEffect } from 'react';
import { Box, CenteredLoader, Container, Grid, isVideo } from 'ui';

import {
  AiGravimetricAnalysisDataCard,
  AiSurfaceAnalysisDataCard,
  LoadingDetailsDataCard,
  MaterialSpecificationsDataCard,
  SiteDataCard,
  TargetBaleWeightsDataCard,
  TransactionHistoryDataCard,
  WeightSampleAnalysisDataCard,
} from '../../../data-cards';
import type { QualityReportTrackingSource } from '../../../types';
import { useQualityReportData } from '../../hooks/useQualityReportData/useQualityReportData';
import { useQualityReportMedia } from '../../hooks/useQualityReportMedia/useQualityReportMedia';
import { useSampleData } from '../../hooks/useSampleData/useSampleData';
import { SiteMaterialQualityReportFooter } from '../SiteMaterialQualityReportFooter/SiteMaterialQualityReportFooter';
import { SiteMaterialQualityReportHeader } from '../SiteMaterialQualityReportHeader/SiteMaterialQualityReportHeader';

export const SiteMaterialQualityReport = ({
  siteMaterialId,
  slotProps,
  placeholderSrc,
  handleTrackView,
  source,
}: {
  siteMaterialId: string;
  slotProps?: {
    header?: Partial<ComponentProps<typeof SiteMaterialQualityReportHeader>>;
  };
  placeholderSrc?: string;
  handleTrackView?: ({ material, materialId }: { material: string; materialId: number }) => void;
  source: QualityReportTrackingSource;
}) => {
  const {
    data,
    error,
    sampleMaterial,
    isLoading: isLoadingData,
    hasEnoughSiteData,
    hasEnoughLoadingData,
    hasEnoughMaterialSpecificationData,
    hasEnoughAiSurfaceAnalysisData,
    hasEnoughAiGravimetricAnalysisData,
  } = useQualityReportData(siteMaterialId);

  const { manualSamples, hasSampleEvidence } = useSampleData(siteMaterialId);
  const {
    evidence,
    balesAccepted,
    isLoading: isLoadingEvidence,
  } = useQualityReportMedia(siteMaterialId, { pageSize: 5 });

  const isLoading = isLoadingData || isLoadingEvidence;

  useEffect(() => {
    if (!isLoading && !isLoadingEvidence && data) {
      handleTrackView?.({ material: data.material, materialId: data.id });
    }
    // We need to run this only once, but after the data is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (isLoading) {
    return (
      <Box height="100dvh">
        <CenteredLoader />
      </Box>
    );
  }

  // Ensure marketing photos are prioritised
  // We're showing only 5 photos but can take from other categories if necessary
  const orderedPhotoEvidence = evidence.marketing.slice(0, 5);
  if (orderedPhotoEvidence.length < 5) {
    orderedPhotoEvidence.push(
      ...[
        ...evidence.site,
        ...evidence.accepted,
        ...evidence.specification,
        ...evidence.loading,
        ...evidence.ai_gravimetric,
      ].slice(0, 5 - orderedPhotoEvidence.length)
    );
  }

  if (error || !data) {
    return <ErrorPage message="Quality Report not available" href="/" />;
  }

  const orderedPhotoEvidencePreviews = orderedPhotoEvidence.map((evidenceItem) => {
    if (isVideo(evidenceItem.url)) {
      return evidenceItem.url;
    }
    return String(evidenceItem.preview || evidenceItem.url);
  });

  return (
    <Container
      sx={(theme) => ({
        backgroundColor: theme.palette.common.white,
        minHeight: `calc(100dvh - ${theme.constants.groundControlNavbarHeight}px)`,
      })}
    >
      <SiteMaterialQualityReportHeader
        originCountry={data.origin_country}
        material={data.material}
        {...slotProps?.header}
        grade={slotProps?.header?.grade || data.grade}
        photoEvidence={orderedPhotoEvidencePreviews}
        placeholderSrc={placeholderSrc}
        source={source}
      />

      <Grid container spacing={4} padding={2}>
        {data.detected_ubc_content.target_bale_weights.length > 0 &&
        data.ai_model !== AiModelEnum.pet &&
        balesAccepted >= 5 ? (
          <Grid item xs={12} sm={6}>
            <TargetBaleWeightsDataCard
              detectedUbcContent={data.detected_ubc_content}
              grade={data.grade}
              siteMaterialId={siteMaterialId}
            />
          </Grid>
        ) : null}

        {hasEnoughAiGravimetricAnalysisData ? (
          <Grid item xs={12} sm={6}>
            <AiGravimetricAnalysisDataCard
              isLoading={isLoading}
              sample={data?.ai_gravimetric_sample}
              composition={data?.ai_gravimetric_composition}
              aiModel={data.ai_model}
              hasMedia={evidence.ai_gravimetric.length > 0}
            />
          </Grid>
        ) : null}

        {hasEnoughAiSurfaceAnalysisData ? (
          <Grid item xs={12} sm={6}>
            <AiSurfaceAnalysisDataCard
              isLoading={isLoading}
              balesAnalysed={Number(balesAccepted)}
              latestInspection={data.latest_inspection}
              composition={data.composition}
              aiModel={data.ai_model}
              hasMedia={evidence.accepted.length > 0}
            />
          </Grid>
        ) : null}

        {manualSamples?.length > 0 && (
          <Grid item xs={12} sm={6}>
            <WeightSampleAnalysisDataCard
              isLoading={isLoading}
              samples={manualSamples}
              sampleMaterial={sampleMaterial}
              hasMedia={hasSampleEvidence}
            />
          </Grid>
        )}

        {hasEnoughMaterialSpecificationData ? (
          <Grid item xs={12}>
            <MaterialSpecificationsDataCard
              isLoading={isLoading}
              storageType={data.specification?.storage_type}
              materialStrapping={data.specification?.material_strapping}
              height={data.specification?.height}
              width={data.specification?.width}
              length={data.specification?.length}
              weight={data.specification?.weight}
              visualAnalysis={data.specification?.visual_analysis}
              radiationAnalysis={data.specification?.radiation_analysis}
              ferrousAnalysis={data.specification?.ferrous_analysis}
              materialAnalysisNotes={data.specification?.material_analysis_notes}
              materialOrigin={data?.material_product?.material_origin?.type}
              materialProcess={data?.material_product?.creation_process}
              hasCapsAndLabels={data?.material_product?.has_caps_and_labels}
              isFoodGrade={data?.material_product?.is_food_grade}
              hasMedia={evidence.specification.length > 0}
            />
          </Grid>
        ) : null}

        {hasEnoughLoadingData ? (
          <Grid item xs={12} sm={6}>
            <LoadingDetailsDataCard
              isLoading={isLoading}
              loadingTechnique={data.specification?.loading_technique}
              targetPayload={data.specification?.typical_container_weight}
              packedOn={data.specification?.packed_on}
              loadingNotes={data.specification?.loading_notes}
              hasMedia={evidence.loading.length > 0}
            />
          </Grid>
        ) : null}

        {hasEnoughSiteData ? (
          <Grid item xs={12} sm={6}>
            <SiteDataCard
              isLoading={isLoading}
              storageConditions={data.specification?.storage_conditions}
              inputMaterialNotes={data.specification?.input_material_notes}
              machinesUsedNotes={data.specification?.machines_used_notes}
              storedOn={data.specification?.stored_on}
              productionVolume={data.specification?.production_volume}
              siteNotes={data.specification?.site_notes}
              hasMedia={evidence.site.length > 0}
            />
          </Grid>
        ) : null}
        <Grid item xs={12} sm={6}>
          <TransactionHistoryDataCard sellerId={data.trade_company_id} />
        </Grid>
      </Grid>
      <SiteMaterialQualityReportFooter siteMaterialId={siteMaterialId} />
    </Container>
  );
};
